import { Button } from "@mui/material";
import { createContext, useContext, useId, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { HumanInTheLoopDecision } from "./human-in-the-loop";

export type ToolApprovalPresentation = {
  container: HTMLDivElement | null;
  selected: boolean;
  showAll: boolean;
  decision: HumanInTheLoopDecision | undefined;
  agentName: string;
  select: () => void;
};

const ToolApprovalContext = createContext<ToolApprovalPresentation | null>(
  null
);
export const ToolApprovalProvider = ToolApprovalContext.Provider;
export const useToolApproval = () => useContext(ToolApprovalContext);

export const ToolApproval = ({
  title,
  children,
  onViewChanges,
}: {
  title: ReactNode;
  children: ReactNode;
  onViewChanges: () => void;
}) => {
  const approval = useToolApproval();
  const titleId = useId();
  const reviewId = useId();
  if (!approval?.container) return null;
  const { container, selected, showAll, decision, agentName, select } =
    approval;
  return createPortal(
    <>
      {showAll && !decision && (
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 py-2">
          <div className="min-w-0">
            <div id={titleId} className="text-sm break-words">
              {title}
            </div>
            <div className="text-xs text-gray-500">
              <span className="text-xs">Requested by: {agentName}</span>
              {agentName !== "Main agent" && (
                <span className="text-xs"> (sub-agent of Main agent)</span>
              )}
            </div>
          </div>
          <Button
            size="small"
            onClick={select}
            aria-labelledby={`${reviewId} ${titleId}`}
          >
            <span id={reviewId}>Review</span>
          </Button>
        </div>
      )}
      <div hidden={!selected || showAll || !!decision}>
        <div className="text-sm break-words">{title}</div>
        <div className="mt-1 text-xs text-gray-500">
          <span className="text-xs">Requested by: {agentName}</span>
          {agentName !== "Main agent" && (
            <span className="text-xs"> (sub-agent of Main agent)</span>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={onViewChanges}
          >
            View changes
          </Button>
          <div role="group" aria-label="Tool approval">
            {children || (
              <span className="text-sm text-gray-500">
                Open the tool to review this request.
              </span>
            )}
          </div>
        </div>
      </div>
    </>,
    container
  );
};
